// Config panel for the "check_output_state" condition block
import React, { useState, useEffect } from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Typography,
  CircularProgress,
  Autocomplete
} from '@mui/material';
import { qbusApi } from '../../../services/apiClient';

const operators = [
  { value: 'eq', label: 'Equals (==)' },
  { value: 'neq', label: 'Not equals (!=)' },
  { value: 'gt', label: 'Greater than (>)' },
  { value: 'lt', label: 'Less than (<)' },
];

const CheckOutputStateConfig = ({ config, locationId, onChange, onSummaryChange }) => {
  const [outputs, setOutputs] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!locationId) return;
    setLoading(true);
    qbusApi.getOutputsForLocation(locationId)
      .then((res) => setOutputs(res.data.outputs || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [locationId]);

  const handleOutputChange = (value) => {
    const updated = { ...config };
    if (typeof value === 'object' && value !== null) {
      updated.outputId = value.id;
      updated.outputName = value.friendlyName || value.name || value.output_id;
    } else {
      updated.outputId = null;
      updated.outputName = value || '';
    }
    onChange(updated);
    const name = updated.outputName || '?';
    const op = operators.find((o) => o.value === updated.operator)?.label || updated.operator || '?';
    onSummaryChange(`${name} ${op} ${updated.value ?? '?'}`);
  };

  const handleChange = (field, value) => {
    const updated = { ...config, [field]: value };
    onChange(updated);
    const name = updated.outputName || `#${updated.outputId || '?'}`;
    const op = operators.find((o) => o.value === updated.operator)?.label || updated.operator || '?';
    onSummaryChange(`${name} ${op} ${updated.value ?? '?'}`);
  };

  if (loading) return <CircularProgress size={24} />;

  const options = outputs.map((o) => ({
    ...o,
    label: `${o.friendlyName || o.name || o.output_id}`,
  }));

  const currentValue = config.outputId
    ? options.find((o) => o.id === config.outputId) || null
    : null;
  const inputValue = config.outputName || '';

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography variant="caption" color="text.secondary">
        Checks another output's current value. Branches TRUE if the condition matches, FALSE otherwise.
      </Typography>

      <Autocomplete
        freeSolo
        options={options}
        getOptionLabel={(option) =>
          typeof option === 'string' ? option : (option.friendlyName || option.name || option.output_id || '')
        }
        value={currentValue}
        inputValue={inputValue}
        onInputChange={(_e, newInputValue) => {
          if (!currentValue || newInputValue !== (currentValue.friendlyName || currentValue.name || currentValue.output_id)) {
            handleOutputChange(newInputValue);
          }
        }}
        onChange={(_e, newValue) => {
          handleOutputChange(newValue);
        }}
        isOptionEqualToValue={(option, value) => option.id === value?.id}
        renderOption={(props, option) => (
          <li {...props} key={option.id}>
            <Box>
              <Typography variant="body2">{option.friendlyName || option.name || option.output_id}</Typography>
              <Typography variant="caption" color="text.secondary">
                {option.output_id} &middot; {option.outputType?.friendlyName || option.function_block_type || ''}
              </Typography>
            </Box>
          </li>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            size="small"
            label="Output"
            required
            helperText="Type an output name or select from the list"
          />
        )}
      />

      <FormControl fullWidth size="small" required>
        <InputLabel>Operator</InputLabel>
        <Select
          value={config.operator || 'eq'}
          onChange={(e) => handleChange('operator', e.target.value)}
          label="Operator"
        >
          {operators.map((op) => (
            <MenuItem key={op.value} value={op.value}>
              {op.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <TextField
        size="small"
        label="Value"
        value={config.value ?? ''}
        onChange={(e) => handleChange('value', e.target.value)}
        required
        helperText="The value to compare against"
      />
    </Box>
  );
};

export default CheckOutputStateConfig;
